/** Takım lideri kıyas — 6 modül, özet metrikler ve asistan detayı */

export const TLP_LEADER_CHART_COLORS = [
  { bar: "bg-emerald-500", text: "text-emerald-600", ring: "ring-emerald-500/30" },
  { bar: "bg-violet-500", text: "text-violet-600", ring: "ring-violet-500/30" },
  { bar: "bg-sky-500", text: "text-sky-600", ring: "ring-sky-500/30" },
  { bar: "bg-amber-500", text: "text-amber-600", ring: "ring-amber-500/30" },
  { bar: "bg-rose-500", text: "text-rose-600", ring: "ring-rose-500/30" },
  { bar: "bg-teal-500", text: "text-teal-600", ring: "ring-teal-500/30" },
  { bar: "bg-indigo-500", text: "text-indigo-600", ring: "ring-indigo-500/30" },
  { bar: "bg-orange-500", text: "text-orange-600", ring: "ring-orange-500/30" },
  { bar: "bg-cyan-500", text: "text-cyan-600", ring: "ring-cyan-500/30" },
  { bar: "bg-fuchsia-500", text: "text-fuchsia-600", ring: "ring-fuchsia-500/30" },
  { bar: "bg-lime-500", text: "text-lime-600", ring: "ring-lime-500/30" },
  { bar: "bg-pink-500", text: "text-pink-600", ring: "ring-pink-500/30" },
  { bar: "bg-blue-500", text: "text-blue-600", ring: "ring-blue-500/30" },
  { bar: "bg-red-500", text: "text-red-600", ring: "ring-red-500/30" },
  { bar: "bg-yellow-500", text: "text-yellow-700", ring: "ring-yellow-500/30" },
];

const avgKalite = (bundle) => {
  const agents = bundle?.modules?.tl_kalite_alignment?.agents || [];
  const withPct = agents.filter((a) => a.alignment?.percent != null);
  if (!withPct.length) return null;
  return Math.round(withPct.reduce((s, a) => s + a.alignment.percent, 0) / withPct.length);
};

const avgTeamKaliteli = (bundle, field) => {
  const teams = bundle?.modules?.kaliteli_conversion?.teams || [];
  const vals = teams.map((t) => t[field]).filter((v) => v != null);
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
};

export const tlpExtractLeaderSummary = (bundle) => {
  const m = bundle?.modules || {};
  const ciro = m.ciro?.totals || {};
  const agentCount = Number(bundle?.agent_count) || 0;
  const islem = Number(ciro.islem) || 0;
  const ciroTotal = Number(ciro.ciro) || 0;
  return {
    agentCount,
    islem,
    muayene: Number(ciro.muayene) || 0,
    iptal: Number(ciro.iptal) || 0,
    ciro: ciroTotal,
    islemPerAgent: agentCount > 0 ? Math.round((islem / agentCount) * 10) / 10 : null,
    ciroPerAgent: agentCount > 0 ? Math.round(ciroTotal / agentCount) : null,
    mrTotal: m.mr?.total_net ?? null,
    mrDailyAvg: m.mr?.daily_avg_net ?? null,
    kaliteAvg: avgKalite(bundle),
    wpAvg: avgTeamKaliteli(bundle, "wp_conversion_avg"),
    disAvg: avgTeamKaliteli(bundle, "dis_arama_conversion_avg"),
    talkAvgSec: m.call_metrics?.team_avg_daily_talk_seconds ?? null,
    breakAvgSec: m.call_metrics?.team_avg_daily_break_seconds ?? null,
    hireInPeriod: m.personnel?.summary?.hire_in_period ?? null,
    exitInPeriod: m.personnel?.summary?.exit_in_period ?? null,
    activeCount: m.personnel?.summary?.active_count ?? agentCount,
    avgExitTenureDays: m.personnel?.summary?.avg_exit_in_period_tenure_days
      ?? m.personnel?.summary?.avg_exit_tenure_days
      ?? null,
    avgExitTenureLabel: m.personnel?.summary?.avg_exit_in_period_tenure_label
      ?? m.personnel?.summary?.avg_exit_tenure_label
      ?? null,
  };
};

/** Tekli sayfadaki 6 modül ile hizalı kıyas kategorileri */
export const TLP_COMPARE_CATEGORIES = [
  {
    id: "mod1",
    title: "1 — Ciro · işlem · muayene · iptal",
    detailKey: "ciro",
    metrics: [
      { key: "islem", label: "Toplam işlem", hint: "Ciro Excel — işlem adedi", format: "int", higherBetter: true },
      { key: "muayene", label: "Muayene", format: "int", higherBetter: true },
      { key: "iptal", label: "İptal", format: "int", higherBetter: false },
      { key: "ciro", label: "Toplam ciro", format: "money", higherBetter: true },
      { key: "islemPerAgent", label: "İşlem / asistan", hint: "Verimlilik", format: "decimal", higherBetter: true },
      { key: "ciroPerAgent", label: "Ciro / asistan", format: "money", higherBetter: true },
    ],
  },
  {
    id: "mod2",
    title: "2 — MR alma (günlük)",
    detailKey: "mr",
    metrics: [
      { key: "mrTotal", label: "MR toplam (net)", format: "int", higherBetter: true },
      { key: "mrDailyAvg", label: "Günlük ort. MR", format: "decimal", higherBetter: true },
    ],
  },
  {
    id: "mod3",
    title: "3 — WP & dış arama dönüşümü",
    detailKey: "kaliteli",
    metrics: [
      { key: "wpAvg", label: "WP dönüşüm (takım ort.)", format: "percent", higherBetter: true },
      { key: "disAvg", label: "Kaliteli işlem (takım ort.)", format: "percent", higherBetter: true },
    ],
  },
  {
    id: "mod4",
    title: "4 — Ort. görüşme & mola",
    detailKey: "calls",
    metrics: [
      { key: "talkAvgSec", label: "Ort. günlük görüşme", format: "duration", higherBetter: null },
      { key: "breakAvgSec", label: "Ort. günlük mola", format: "duration", higherBetter: null },
    ],
  },
  {
    id: "mod5",
    title: "5 — TL × Kalite eşleşme",
    detailKey: "kalite",
    metrics: [
      { key: "kaliteAvg", label: "Uyum ortalaması", format: "percent", higherBetter: true },
    ],
  },
  {
    id: "mod6",
    title: "6 — Personel giriş / çıkış",
    detailKey: "personnel",
    metrics: [
      { key: "agentCount", label: "Takım asistan sayısı", format: "int", higherBetter: null },
      { key: "activeCount", label: "Aktif (dönem sonu)", format: "int", higherBetter: null },
      { key: "hireInPeriod", label: "Dönemde işe giriş", format: "int", higherBetter: null },
      { key: "exitInPeriod", label: "Dönemde çıkış", format: "int", higherBetter: false },
      {
        key: "avgExitTenureDays",
        label: "Ortalama işten çıkarma süresi",
        hint: "Önce dönem içi çıkışların ort. süresi; yoksa takımdaki tüm çıkışların ort.",
        format: "days",
        higherBetter: null,
      },
    ],
  },
];

export const TLP_COMPARE_METRICS = TLP_COMPARE_CATEGORIES.flatMap((c) => c.metrics.map((m) => ({
  ...m,
  detailKey: c.detailKey,
  categoryId: c.id,
})));

export const tlpFormatCompareValue = (format, value) => {
  if (value == null || value === "") return "—";
  switch (format) {
    case "money":
      return `₺${Number(value).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
    case "percent":
      return `%${value}`;
    case "decimal":
      return Number(value).toLocaleString("tr-TR", { maximumFractionDigits: 1 });
    case "duration": {
      const s = Math.max(0, Number(value) || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (h > 0) return `${h}s ${m}dk`;
      return `${m} dk`;
    }
    case "days":
      return `${Number(value).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} gün`;
    case "int":
    default:
      return Number(value).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
  }
};

const formatDurationExport = (sec) => {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}s ${m}dk`;
  return `${m} dk`;
};

const aggregateCiroByAgent = (rows) => {
  const map = new Map();
  (rows || []).forEach((r) => {
    const id = r.agent_id ?? r.assistant_name;
    const prev = map.get(id) || {
      agent_id: r.agent_id,
      name: r.assistant_name,
      islem: 0,
      muayene: 0,
      iptal: 0,
      ciro: 0,
      has_left: false,
      roster_zero_fill: false,
    };
    prev.islem += Number(r.islem_adeti) || 0;
    prev.muayene += Number(r.muayene_adeti) || 0;
    prev.iptal += Number(r.iptal_adeti) || 0;
    prev.ciro += Number(r.toplam_ciro) || 0;
    prev.has_left = prev.has_left || Boolean(r.has_left);
    prev.roster_zero_fill = prev.roster_zero_fill || Boolean(r.roster_zero_fill);
    map.set(id, prev);
  });
  return [...map.values()].sort((a, b) => b.ciro - a.ciro);
};

export const tlpGetLeaderDetailRows = (bundle, detailKey) => {
  const m = bundle?.modules || {};
  switch (detailKey) {
    case "ciro":
      return aggregateCiroByAgent(m.ciro?.rows).map((r) => ({
        name: r.name,
        meta: r.has_left ? "İşten ayrıldı" : (r.roster_zero_fill ? "Excel kaydı yok" : null),
        cols: [
          ["İşlem", r.islem],
          ["Muayene", r.muayene],
          ["İptal", r.iptal],
          ["Ciro", r.ciro],
        ],
      }));
    case "mr":
      return (m.mr?.agents || []).map((a) => ({
        name: a.assistant_name,
        cols: [
          ["Toplam MR", a.total_net],
          ["Günlük ort.", a.daily_avg_net],
          ["MR alma", a.mr_alma_orani_display || "—"],
        ],
      }));
    case "kalite":
      return (m.tl_kalite_alignment?.agents || []).map((a) => ({
        name: a.agent_name,
        cols: [
          ["Uyum %", a.alignment?.percent != null ? `%${a.alignment.percent}` : "—"],
          ["Uyumsuz alan", (a.alignment?.diffs || []).length || (a.alignment?.criteria || []).filter((c) => c.mismatch).length || "—"],
          ["Eski data", a.eski_data_aliyor ? "Evet" : "Hayır"],
        ],
      }));
    case "kaliteli": {
      const rows = [];
      (m.kaliteli_conversion?.teams || []).forEach((t) => {
        (t.agents || []).forEach((a) => {
          rows.push({
            name: a.agent_name,
            cols: [
              ["WP sayı", a.wp_data_count ?? "—"],
              ["WP dönüşüm", a.wp_conversion != null ? `%${a.wp_conversion}` : "—"],
              ["Kaliteli data", a.kaliteli_data_count ?? "—"],
              ["Dış arama dön.", a.dis_arama_conversion != null ? `%${a.dis_arama_conversion}` : "—"],
            ],
          });
        });
      });
      return rows;
    }
    case "calls":
      return (m.call_metrics?.agents || []).map((a) => ({
        name: a.agent_name,
        cols: [
          ["Görüşme/gün", formatDurationExport(a.avg_daily_talk_seconds)],
          ["Mola/gün", formatDurationExport(a.avg_daily_break_seconds)],
        ],
      }));
    case "personnel":
      return (m.personnel?.agents || []).map((a) => ({
        name: a.agent_name,
        meta: a.has_left || a.is_active === false ? "Ayrıldı" : null,
        cols: [
          ["İşe giriş", a.hire_date || "—"],
          ["Çıkış", a.exit_date || "—"],
          ["Süre", a.exit_date ? (a.tenure_until_exit_label || "—") : (a.tenure_label || "—")],
        ],
      }));
    default:
      return [];
  }
};

export const TLP_DETAIL_TITLES = {
  ciro: "Asistan — ciro · işlem · muayene · iptal",
  mr: "Asistan — MR (günlük)",
  kalite: "Asistan — TL × Kalite",
  kaliteli: "Asistan — WP & Kaliteli",
  calls: "Asistan — görüşme & mola",
  personnel: "Asistan — personel",
};
